import { Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { BroadcastKey, BroadcastService } from '@services/broadcast/';
import { FeedbackData, FeedbackService, FeedbackStyle } from '@services/feedback/';
import { ISystemAlert, SystemAlertAction, SystemAlertsService, SystemAlertType } from '@services/system-alerts/';
import { componentDestroyed, OnDestroyMixin } from '@w11k/ngx-componentdestroyed';
import each from 'lodash/each';
import filter from 'lodash/filter';
import last from 'lodash/last';
import { takeUntil } from 'rxjs/operators';

@Component({
	selector: 'app-system-alert-banner',
	templateUrl: './alert-banner.component.html',
	styleUrls: ['./alert-banner.component.scss']
})
export class AlertBannerComponent extends OnDestroyMixin implements OnInit {
	@Input() types: SystemAlertType[];

	alerts: ISystemAlert[] = [];

	currentRoutePath = '';

	constructor(
		private systemAlertsService: SystemAlertsService,
		private broadcastService: BroadcastService,
		private feedbackService: FeedbackService,
		private router: Router
	) {
		super();
		this.router.events.pipe(takeUntil(componentDestroyed(this))).subscribe((res) => {
			this.currentRoutePath = this.router.url;
		});
	}

	get activateLink(): boolean {
		return this.displayAlert.link && !this.currentRoutePath.endsWith(this.displayAlert.link);
	}

	get displayAlert(): ISystemAlert {
		return last(this.alerts);
	}

	ngOnInit(): void {
		this.broadcastService
			.on<ISystemAlert>(BroadcastKey.system_alert_new)
			.pipe(takeUntil(componentDestroyed(this)))
			.subscribe((alert: ISystemAlert) => {
				this.addAlert(alert);
			});
		this.broadcastService
			.on<ISystemAlert>(BroadcastKey.system_alert_cancelled)
			.pipe(takeUntil(componentDestroyed(this)))
			.subscribe((alert: ISystemAlert) => {
				this.removeAlert(alert);
			});
		this.addAlerts(this.systemAlertsService.allAlerts);
	}

	private addAlerts(alerts: ISystemAlert[]) {
		each(alerts, (alert: ISystemAlert) => {
			this.addAlert(alert);
		});
	}

	private addAlert(alert: ISystemAlert) {
		// only add aletrts if it's type is listed in 'types' array
		const allowAll = this.types.includes(SystemAlertType.all);
		if (allowAll || this.types.includes(alert.type)) {
			this.removeAlert(alert); // first remove in case it alreayd existed
			this.alerts.push(alert);
		}
	}

	navigate(alert: ISystemAlert) {
		if (alert.link) {
			this.router.navigate([alert.link]);
		}
	}

	callback(alert: ISystemAlert) {
		if (alert.callback) {
			alert.callback();
		}
	}

	alertMessage(alert: ISystemAlert) {
		const data: FeedbackData = {
			title: alert.title,
			message: alert.message,
			style: FeedbackStyle.error
		};
		this.feedbackService.plain(data);
	}

	alertCancel(alert: ISystemAlert) {
		if (alert.cancellable) {
			this.systemAlertsService.cancel(alert.id, SystemAlertAction.completed);
		}
	}

	private removeAlert(alert: ISystemAlert) {
		this.alerts = filter(this.alerts, (a: ISystemAlert) => {
			return a.id !== alert.id;
		});
	}
}
